import { useParams } from 'react-router-dom';
import { useFilterStore } from './searchFilterPanel/FilterStoreProvider';
import useSortStore from './searchSort/SortStore';
import { CATEGORY_ALL } from '../discover/discoverConstants';
import { decodeCategoryName } from './searchUtils';
import { FILTER_KEYS } from '../../constants/constants';
import useFilterSortUrlSync from '../../hook/useFilterSortUrlSync';
import useCategoryNames from '../../query/useCategoryNames';

/**
 * 검색 페이지의 복잡한 상태 관리 및 비즈니스 로직을 처리하는 커스텀 훅
 * @returns {object} 검색 페이지 렌더링에 필요한 상태와 핸들러
 */
export function useSearchManager() {
    const { keyword, categoryName: partiallyEncodedCategoryName = CATEGORY_ALL.name } = useParams();
    const categoryName = decodeCategoryName(partiallyEncodedCategoryName);

    const { isCategoryMatched, loading: categoryLoading } = useCategoryNames();

    const filters = useFilterStore(state => state.filters);
    const updateFilter = useFilterStore(state => state.updateFilter);
    const clearAllFilters = useFilterStore(state => state.clearAllFilters);

    const selectedSort = useSortStore(state => state.selectedSort);
    const setSort = useSortStore(state => state.setSort);

    // 카테고리 로딩 중에는 POPULAR 유지, 로딩 완료 후 매칭 판별
    const defaultSort = !keyword || categoryLoading || isCategoryMatched(keyword) ? 'POPULAR' : 'RELEVANCE';
    // URL <-> 상태 공용 동기화
    useFilterSortUrlSync(defaultSort);

    // 이벤트 핸들러
    const handleSortChange = (sortCode) => setSort(sortCode);

    const handleRemoveFilter = (filterType, value) => {
        switch (filterType) {
            case FILTER_KEYS.GENDER:
                updateFilter(FILTER_KEYS.GENDER, '');
                break;
            case FILTER_KEYS.SUBCATEGORY_IDS:
                updateFilter(FILTER_KEYS.SUBCATEGORY_IDS, filters[FILTER_KEYS.SUBCATEGORY_IDS].filter(id => id !== value));
                break;
            case FILTER_KEYS.BRANDS:
                updateFilter(FILTER_KEYS.BRANDS, filters[FILTER_KEYS.BRANDS].filter(brand => {
                    const brandName = typeof brand === 'string' ? brand : brand.name;
                    const valueName = typeof value === 'string' ? value : value.name;
                    return brandName !== valueName;
                }));
                break;
            case 'price': // price는 min/max 두 개라 상수가 없음
                updateFilter(FILTER_KEYS.PRICE_MIN, null);
                updateFilter(FILTER_KEYS.PRICE_MAX, null);
                break;
            case FILTER_KEYS.IS_SELLING:
                updateFilter(FILTER_KEYS.IS_SELLING, false);
                break;
            case FILTER_KEYS.CONDITION:
                updateFilter(FILTER_KEYS.CONDITION, '');
                break;
            case FILTER_KEYS.SIZE_VALUE_IDS: {
                const removeIds = new Set((value?.valueIds || []).map(Number));
                const remaining = (filters[FILTER_KEYS.SIZE_VALUE_IDS] || [])
                    .filter((id) => !removeIds.has(Number(id)));
                updateFilter(FILTER_KEYS.SIZE_VALUE_IDS, remaining);
                break;
            }
            default:
                break;
        }
    };

    const handleClearAllFilters = () => {
        clearAllFilters();
        setSort(defaultSort);
    };

    return {
        keyword,
        categoryName,
        filters,
        selectedSort,
        handleSortChange,
        handleRemoveFilter,
        handleClearAllFilters,
    };
}
